import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Slider,
  Stack,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

interface VideoTrimmerProps {
  videoUrl: string;
  initialStart: number;
  initialEnd: number;
  onTimeChange: (start: number, end: number) => void;
  maxDuration?: number;
}

export default function VideoTrimmer({
  videoUrl,
  initialStart,
  initialEnd,
  onTimeChange,
  maxDuration = 1800, // 30 minutes
}: VideoTrimmerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialStart);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimRange, setTrimRange] = useState([initialStart, initialEnd]);
  const [zoomRange, setZoomRange] = useState([0, videoDuration]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const MIN_GAP = 0.1;

  // Update trim range when initial values change
  useEffect(() => {
    setTrimRange([initialStart, initialEnd]);
    setCurrentTime(initialStart);
    setZoomRange([0, videoRef.current?.duration || 0]);
    setIsZoomed(false);
  }, [initialStart, initialEnd]);

  // Reset loading state when video URL changes
  useEffect(() => {
    setIsVideoLoading(true);
  }, [videoUrl]);

  const togglePlay = async () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          // Handle play interruption gracefully
          console.log("Play was interrupted:", error);
          setIsPlaying(false);
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;

      // Keep video within trim bounds
      if (time < trimRange[0]) {
        // eslint-disable-next-line prefer-destructuring
        videoRef.current.currentTime = trimRange[0];
        setCurrentTime(trimRange[0]);
      } else if (time > trimRange[1]) {
        // eslint-disable-next-line prefer-destructuring
        videoRef.current.currentTime = trimRange[1];
        // Only pause if we're currently playing to avoid interrupting play() calls
        if (isPlaying) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
        setCurrentTime(trimRange[1]);
      } else {
        setCurrentTime(time);
      }
    }
  };

  const handleTrimChange = (event: Event, newValue: number | number[]) => {
    const range = newValue as number[];
    const start = Math.max(0, Math.min(range[0], videoDuration - MIN_GAP));
    const end = Math.min(videoDuration, Math.max(range[1], start + MIN_GAP));
    const next = [start, end] as [number, number];
    setTrimRange(next);
    onTimeChange(next[0], next[1]);

    // Update video position if current time is outside new range
    if (videoRef.current) {
      if (currentTime < next[0] || currentTime > next[1]) {
        // eslint-disable-next-line prefer-destructuring
        videoRef.current.currentTime = next[0];
        setCurrentTime(next[0]);
      }
    }
  };

  const handleZoomedTrimChange = (
    event: Event,
    newValue: number | number[],
  ) => {
    const range = newValue as number[];
    const start = Math.max(
      zoomRange[0],
      Math.min(range[0], zoomRange[1] - MIN_GAP),
    );
    const end = Math.min(zoomRange[1], Math.max(range[1], start + MIN_GAP));
    const next = [start, end] as [number, number];
    setTrimRange(next);
    onTimeChange(next[0], next[1]);
  };

  const handleVideoSeek = (time: number) => {
    if (videoRef.current) {
      // Constrain seek to trim range
      const constrainedTime = Math.max(
        trimRange[0],
        Math.min(trimRange[1], time),
      );
      videoRef.current.currentTime = constrainedTime;
      setCurrentTime(constrainedTime);
    }
  };

  const zoomOut = () => {
    setZoomRange([0, videoDuration]);
    setIsZoomed(false);
  };

  const zoomToTrimRange = () => {
    const range = trimRange[1] - trimRange[0];
    const padding = range * 0.2; // Add 20% padding
    const start = Math.max(0, trimRange[0] - padding);
    const end = Math.min(videoDuration, trimRange[1] + padding);
    setZoomRange([start, end]);
    setIsZoomed(true);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const trimDuration = trimRange[1] - trimRange[0];

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <ContentCutIcon />
        Video Trimmer
      </Typography>

      {/* Video Player */}
      <Box
        sx={{
          mb: 2,
          bgcolor: "black",
          borderRadius: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <video
          ref={videoRef}
          width="100%"
          height="300"
          src={videoUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadStart={() => setIsVideoLoading(true)}
          onCanPlay={() => setIsVideoLoading(false)}
          onLoadedMetadata={() => {
            if (videoRef.current) {
              setVideoDuration(videoRef.current.duration);
              videoRef.current.currentTime = initialStart;
              setZoomRange([0, videoRef.current.duration]);
              setIsVideoLoading(false);
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          style={{ display: "block" }}
        >
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>

        {/* Video Loading Overlay */}
        {isVideoLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: "white" }} />
            <Typography variant="body2">Loading video...</Typography>
          </Box>
        )}
      </Box>

      {/* Custom Controls */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={togglePlay}
            startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            size="small"
          >
            {isPlaying ? "Pause" : "Play"}
          </Button>

          <Typography variant="body2">
            {formatTime(currentTime)} / {formatTime(videoDuration)}
          </Typography>

          <Button
            variant="text"
            size="small"
            onClick={() =>
              handleVideoSeek(Math.max(trimRange[0], currentTime - 10))
            }
            disabled={currentTime <= trimRange[0]}
          >
            -10s
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => handleVideoSeek(trimRange[0])}
          >
            Go to Start
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => handleVideoSeek(trimRange[1])}
          >
            Go to End
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() =>
              handleVideoSeek(Math.min(trimRange[1], currentTime + 10))
            }
            disabled={currentTime >= trimRange[1]}
          >
            +10s
          </Button>
        </Box>

        {/* Playback Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom>
            Video Progress
          </Typography>
          <Box
            sx={{
              position: "relative",
              height: 16,
              bgcolor: "grey.300",
              borderRadius: 6,
              cursor: "pointer",
            }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const clickPercent = clickX / rect.width;
              const clickTime = clickPercent * videoDuration;
              handleVideoSeek(clickTime);
            }}
          >
            {/* Trim range highlight */}
            <Box
              sx={{
                position: "absolute",
                left: `${(trimRange[0] / videoDuration) * 100}%`,
                width: `${((trimRange[1] - trimRange[0]) / videoDuration) * 100}%`,
                height: "100%",
                bgcolor: "primary.light",
                borderRadius: "inherit",
                opacity: 0.6,
                pointerEvents: "none",
              }}
            />
            {/* Current time indicator */}
            <Box
              sx={{
                position: "absolute",
                left: `${(currentTime / videoDuration) * 100}%`,
                width: 3,
                height: "100%",
                bgcolor: "primary.main",
                borderRadius: 1.5,
                pointerEvents: "none",
                boxShadow: "0 0 4px rgba(0,0,0,0.3)",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Zoom Controls */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" sx={{ minWidth: "fit-content" }}>
          Zoom:{" "}
          {isZoomed
            ? `${formatTime(zoomRange[0])} - ${formatTime(zoomRange[1])}`
            : "Full View"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Zoom to trim range">
            <IconButton size="small" onClick={zoomToTrimRange}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out to full duration">
            <span>
              <IconButton size="small" onClick={zoomOut} disabled={!isZoomed}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Dynamic Trim Range Slider */}
      <Box sx={{ mb: 2, px: 1.5 }}>
        <Typography variant="caption" gutterBottom>
          Trim Range: {formatTime(trimRange[0])} - {formatTime(trimRange[1])}{" "}
          (Duration: {formatTime(trimDuration)})
          {isZoomed && " - Zoomed for precision"}
        </Typography>
        <Slider
          value={trimRange}
          onChange={isZoomed ? handleZoomedTrimChange : handleTrimChange}
          valueLabelDisplay="auto"
          valueLabelFormat={formatTime}
          min={isZoomed ? zoomRange[0] : 0}
          max={isZoomed ? zoomRange[1] : videoDuration}
          step={isZoomed ? 0.01 : 0.1}
          sx={{ mt: 1 }}
          marks={[
            {
              value: isZoomed ? zoomRange[0] : 0,
              label: formatTime(isZoomed ? zoomRange[0] : 0),
            },
            {
              value: isZoomed ? zoomRange[1] : videoDuration,
              label: formatTime(isZoomed ? zoomRange[1] : videoDuration),
            },
          ]}
        />
      </Box>

      {/* Trim Info */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Start Time
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatTime(trimRange[0])}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            End Time
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatTime(trimRange[1])}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Duration
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={trimDuration > maxDuration ? "error.main" : "text.primary"}
          >
            {formatTime(trimDuration)}
          </Typography>
        </Box>
      </Box>

      {trimDuration > maxDuration && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Video duration exceeds maximum allowed length of{" "}
          {formatTime(maxDuration)}
        </Alert>
      )}
    </Paper>
  );
}
